/**
 * Contact Sidebar — inline-editable sidebar for the contact detail page.
 * Shows contact info, CRM metrics, tags, AI insights, loyalty, and quick actions.
 */

import { useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Mail, Phone, Globe, Calendar, ShoppingCart, DollarSign,
  TrendingUp, Award, Tag, Pencil, Check, X,
  MessageCircle, User as UserIcon,
  Ban, RotateCcw, Trash2, CheckCircle,
} from 'lucide-react'
import { useCRM } from '@/hooks/useCRM'
import type { CrmContactDetail, CrmContactUpdate, Pipeline } from '@/types/crm'
import { useFeature } from '@/hooks/useFeature'
import { usePermission } from '@/hooks/usePermission'
import { formatCents } from '@/utils/currency'
import { AiInsightsPanel } from '@/components/crm/AiInsightsPanel'
import { AddressesSection } from '@/components/crm/AddressesSection'
import { MergeContactSection } from '@/components/crm/MergeContactSection'
import { DealCreateModal } from '@/components/crm/DealCreateModal'
import {
  Badge, Button, Input, TagInput, Textarea, useToast,
  Modal, ModalContent, ModalHeader, ModalTitle, ModalBody, ModalFooter, Spinner,
} from '@/components/ui'

interface ContactSidebarProps {
  contact: CrmContactDetail
}

export function ContactSidebar({ contact }: ContactSidebarProps) {
  const navigate = useNavigate()
  const { updateContact, fetchPipelines, blockContact, unblockContact, deleteContact, restoreContact } = useCRM()
  const { addToast } = useToast()
  const canWrite = usePermission('crm:write')
  const canDelete = usePermission('crm:delete')
  const activitiesEnabled = useFeature('crm_activities')
  const dealsEnabled = useFeature('crm_deals')
  const aiEnabled = useFeature('crm_ai')

  const [isEditingNotes, setIsEditingNotes] = useState(false)
  const [notesValue, setNotesValue] = useState(contact.notes || '')
  const [showDealModal, setShowDealModal] = useState(false)
  const [dealPipeline, setDealPipeline] = useState<Pipeline | null>(null)
  const [draftTags, setDraftTags] = useState<string[] | null>(null)
  const pendingTagSaves = useRef(0)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deletePolicy, setDeletePolicy] = useState<'soft_delete' | 'anonymize'>('soft_delete')
  const [isDeleting, setIsDeleting] = useState(false)

  const [isEditingInfo, setIsEditingInfo] = useState(false)
  const [infoDraft, setInfoDraft] = useState({
    displayName: '', email: '', birthday: '', languagePreference: '',
  })

  const startEditInfo = useCallback(() => {
    setInfoDraft({
      displayName: contact.displayName ?? '',
      email: contact.email ?? '',
      birthday: contact.birthday ?? '',
      languagePreference: contact.languagePreference ?? '',
    })
    setIsEditingInfo(true)
  }, [contact])

  const handleSaveInfo = useCallback(async () => {
    const norm = (v: string) => v.trim() || null
    const updates: CrmContactUpdate = {}
    if (norm(infoDraft.displayName) !== (contact.displayName ?? null)) updates.displayName = norm(infoDraft.displayName)
    if (norm(infoDraft.email) !== (contact.email ?? null)) updates.email = norm(infoDraft.email)
    if (norm(infoDraft.birthday) !== (contact.birthday ?? null)) updates.birthday = norm(infoDraft.birthday)
    if (norm(infoDraft.languagePreference) !== (contact.languagePreference ?? null)) updates.languagePreference = norm(infoDraft.languagePreference)
    if (Object.keys(updates).length === 0) {
      setIsEditingInfo(false)
      return
    }
    const updated = await updateContact(contact.id, updates)
    if (updated) {
      setIsEditingInfo(false)
      addToast({ type: 'success', title: 'Contact updated' })
    } else {
      addToast({
        type: 'error',
        title: 'Failed to update contact',
        description: 'Check your connection and try again.',
      })
    }
  }, [contact, infoDraft, updateContact, addToast])

  const handleSaveNotes = useCallback(async () => {
    const updated = await updateContact(contact.id, { notes: notesValue })
    if (updated) {
      setIsEditingNotes(false)
      addToast({ type: 'success', title: 'Notes saved' })
    } else {
      addToast({
        type: 'error',
        title: 'Failed to save notes',
        description: 'Check your connection and try again.',
      })
    }
  }, [contact.id, notesValue, updateContact, addToast])

  const handleTagsChange = useCallback(async (tags: string[]) => {
    setDraftTags(tags)
    pendingTagSaves.current += 1
    const updated = await updateContact(contact.id, { tags })
    pendingTagSaves.current -= 1
    if (!updated) {
      addToast({
        type: 'error',
        title: 'Failed to update tags',
        description: 'Check your connection and try again.',
      })
    }
    if (pendingTagSaves.current === 0) {
      setDraftTags(null)
    }
  }, [contact.id, updateContact, addToast])

  const handleCreateDeal = useCallback(async () => {
    const pipelines = await fetchPipelines()
    const target = pipelines?.[0] ?? null
    if (!target) {
      addToast({
        type: 'warning',
        title: "Couldn't open deal form",
        description: 'No pipeline available — create one in Deals, or retry if this looks like a connection issue.',
      })
      return
    }
    setDealPipeline(target)
    setShowDealModal(true)
  }, [fetchPipelines, addToast])

  const handleToggleBlock = useCallback(async () => {
    const action = contact.isBlocked ? unblockContact : blockContact
    const updated = await action(contact.id)
    if (updated) {
      addToast({ type: 'success', title: updated.isBlocked ? 'Contact blocked' : 'Contact unblocked' })
    } else {
      addToast({
        type: 'error',
        title: 'Failed to update block status',
        description: 'Check your connection and try again.',
      })
    }
  }, [contact.id, contact.isBlocked, blockContact, unblockContact, addToast])

  const handleRestore = useCallback(async () => {
    const updated = await restoreContact(contact.id)
    if (updated) {
      addToast({ type: 'success', title: 'Contact restored' })
    } else {
      addToast({
        type: 'error',
        title: 'Failed to restore contact',
        description: 'Check your connection and try again.',
      })
    }
  }, [contact.id, restoreContact, addToast])

  const closeDeleteModal = useCallback(() => {
    setShowDeleteModal(false)
    setDeletePolicy('soft_delete')
  }, [])

  const handleDelete = useCallback(async () => {
    setIsDeleting(true)
    const updated = await deleteContact(contact.id, deletePolicy)
    setIsDeleting(false)
    if (updated) {
      closeDeleteModal()
      addToast({ type: 'success', title: 'Contact deleted' })
    } else {
      addToast({
        type: 'error',
        title: 'Failed to delete contact',
        description: 'Check your connection and try again.',
      })
    }
  }, [contact.id, deletePolicy, deleteContact, addToast, closeDeleteModal])

  return (
    <div className="p-4 space-y-5">
      {/* Contact Info */}
      <Section
        title="Contact Info"
        action={canWrite && !isEditingInfo ? (
          <Button variant="ghost" size="sm" aria-label="Edit contact info" onClick={startEditInfo}>
            <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
          </Button>
        ) : undefined}
      >
        {isEditingInfo ? (
          <div className="space-y-2">
            <Input label="Name" value={infoDraft.displayName}
              onChange={(e) => setInfoDraft((d) => ({ ...d, displayName: e.target.value }))} />
            <Input label="Email" type="email" value={infoDraft.email}
              onChange={(e) => setInfoDraft((d) => ({ ...d, email: e.target.value }))} />
            <Input label="Birthday" type="date" value={infoDraft.birthday}
              onChange={(e) => setInfoDraft((d) => ({ ...d, birthday: e.target.value }))} />
            <Input label="Language" maxLength={10} hint="Locale code, e.g. es_MX"
              value={infoDraft.languagePreference}
              onChange={(e) => setInfoDraft((d) => ({ ...d, languagePreference: e.target.value }))} />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" aria-label="Cancel editing contact info"
                onClick={() => setIsEditingInfo(false)}>
                <X className="w-4 h-4 text-muted-foreground" />
              </Button>
              <Button variant="ghost" size="sm" aria-label="Save contact info" onClick={handleSaveInfo}>
                <Check className="w-4 h-4 text-success" />
              </Button>
            </div>
          </div>
        ) : (
          <>
            {contact.displayName && <InfoRow icon={UserIcon} label="Name" value={contact.displayName} />}
            <InfoRow icon={Phone} label="Phone" value={contact.channelUserId} />
            {contact.email && <InfoRow icon={Mail} label="Email" value={contact.email} />}
            {contact.languagePreference && (
              <InfoRow icon={Globe} label="Language" value={contact.languagePreference} />
            )}
            {contact.birthday && (
              <InfoRow icon={Calendar} label="Birthday" value={contact.birthday} />
            )}
          </>
        )}
      </Section>

      {/* Addresses */}
      <AddressesSection contact={contact} />

      {/* CRM Metrics */}
      <Section title="Customer Value">
        <div className="grid grid-cols-2 gap-3">
          <MetricCard
            label="Lifetime Value"
            value={formatCents(contact.totalSpentCents, contact.currency)}
            icon={DollarSign}
          />
          <MetricCard
            label="Orders"
            value={String(contact.totalOrders)}
            icon={ShoppingCart}
          />
          <MetricCard
            label="Avg. Order"
            value={contact.averageOrderCents > 0
              ? formatCents(contact.averageOrderCents, contact.currency)
              : '—'}
            icon={TrendingUp}
          />
          <MetricCard
            label="Conversations"
            value={String(contact.conversationCount)}
            icon={MessageCircle}
          />
        </div>
      </Section>

      {/* AI Insights Panel */}
      {aiEnabled && (
        <AiInsightsPanel key={contact.id} contact={contact} />
      )}

      {/* Tags */}
      {canWrite ? (
        <Section title="Tags">
          <TagInput value={draftTags ?? contact.tags ?? []} onChange={handleTagsChange} />
        </Section>
      ) : (
        contact.tags && contact.tags.length > 0 && (
          <Section title="Tags">
            <div className="flex flex-wrap gap-1.5">
              {contact.tags.map((tag) => (
                <Badge key={tag} variant="secondary">
                  <Tag className="w-3 h-3 mr-1" />
                  {tag}
                </Badge>
              ))}
            </div>
          </Section>
        )
      )}

      {/* Loyalty */}
      {contact.loyaltyTier && (
        <Section title="Loyalty" icon={Award}>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground capitalize">
              {contact.loyaltyTier}
            </span>
            {contact.loyaltyPoints > 0 && (
              <span className="text-xs text-muted-foreground">
                {contact.loyaltyPoints} points
              </span>
            )}
          </div>
        </Section>
      )}

      {/* Notes */}
      <Section title="Notes">
        {isEditingNotes ? (
          <div className="space-y-2">
            <Textarea
              value={notesValue}
              onChange={(e) => setNotesValue(e.target.value)}
              className="resize-none"
              rows={4}
              autoFocus
              aria-label="Notes"
            />
            <div className="flex gap-2 justify-end">
              <Button
                variant="ghost"
                size="sm"
                aria-label="Cancel editing notes"
                onClick={() => { setIsEditingNotes(false); setNotesValue(contact.notes || '') }}
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                aria-label="Save notes"
                onClick={handleSaveNotes}
              >
                <Check className="w-4 h-4 text-success" />
              </Button>
            </div>
          </div>
        ) : canWrite ? (
          <div
            onClick={() => setIsEditingNotes(true)}
            className="group cursor-pointer p-2 rounded-lg hover:bg-muted transition-colors min-h-[40px]"
          >
            {contact.notes ? (
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{contact.notes}</p>
            ) : (
              <p className="text-sm text-muted-foreground italic flex items-center gap-1">
                <Pencil className="w-3 h-3" /> Click to add notes
              </p>
            )}
          </div>
        ) : contact.notes ? (
          <p className="text-sm text-muted-foreground whitespace-pre-wrap p-2">{contact.notes}</p>
        ) : (
          <p className="text-sm text-muted-foreground italic p-2">No notes</p>
        )}
      </Section>

      {/* Quick Actions */}
      <Section title="Quick Actions">
        <div className="space-y-1.5">
          {activitiesEnabled && canWrite && (
            <QuickActionButton label="Add Note" icon={Pencil} onClick={() => setIsEditingNotes(true)} />
          )}
          {dealsEnabled && (
            <QuickActionButton label="Create Deal" icon={TrendingUp} onClick={handleCreateDeal} />
          )}
          <QuickActionButton
            label="View Conversations"
            icon={MessageCircle}
            onClick={() => navigate(`/inbox?contact=${contact.id}`)}
          />
          <QuickActionButton
            label="View Orders"
            icon={ShoppingCart}
            onClick={() => navigate(`/orders?contact_id=${contact.id}`)}
          />
        </div>
      </Section>

      <MergeContactSection contact={contact} />

      {/* Moderation — block/restore at crm:write, delete at crm:delete */}
      {(canWrite || canDelete) && (
        <Section title="Moderation">
          <div className="space-y-1.5">
            {canWrite && (
              <QuickActionButton
                label={contact.isBlocked ? 'Unblock Contact' : 'Block Contact'}
                icon={contact.isBlocked ? CheckCircle : Ban}
                onClick={handleToggleBlock}
              />
            )}
            {canWrite && contact.isDeleted && (
              <QuickActionButton label="Restore Contact" icon={RotateCcw} onClick={handleRestore} />
            )}
            {canDelete && !contact.isDeleted && (
              <QuickActionButton label="Delete Contact" icon={Trash2} onClick={() => setShowDeleteModal(true)} />
            )}
          </div>
        </Section>
      )}

      {showDealModal && dealPipeline && (
        <DealCreateModal
          pipeline={dealPipeline}
          initialContactId={contact.id}
          onClose={() => setShowDealModal(false)}
        />
      )}

      <Modal
        open={showDeleteModal}
        onOpenChange={(open) => {
          if (open) {
            setShowDeleteModal(true)
          } else {
            closeDeleteModal()
          }
        }}
      >
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Delete Contact</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <p className="text-sm text-foreground mb-4">
              Delete <strong>{contact.displayName || contact.channelUserId}</strong>?
              Choose a deletion policy:
            </p>
            <div className="space-y-3">
              <label className="flex items-start gap-3 p-3 rounded-lg border border-border cursor-pointer hover:bg-muted">
                <input
                  type="radio"
                  name="crmDeletePolicy"
                  value="soft_delete"
                  checked={deletePolicy === 'soft_delete'}
                  onChange={() => setDeletePolicy('soft_delete')}
                  className="mt-1"
                />
                <div>
                  <p className="font-medium text-sm">Soft Delete</p>
                  <p className="text-xs text-muted-foreground">
                    Hide the contact from lists. Can be restored later. Conversations are preserved.
                  </p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-lg border border-destructive/20 cursor-pointer hover:bg-destructive/10">
                <input
                  type="radio"
                  name="crmDeletePolicy"
                  value="anonymize"
                  checked={deletePolicy === 'anonymize'}
                  onChange={() => setDeletePolicy('anonymize')}
                  className="mt-1"
                />
                <div>
                  <p className="font-medium text-sm text-destructive">
                    Anonymize (Permanent)
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Remove all PII (name, phone). Hash the identifier. Cannot be undone.
                    Conversations are kept but customer info is stripped.
                  </p>
                </div>
              </label>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="secondary" onClick={closeDeleteModal}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? <Spinner size="sm" className="mr-1" /> : null}
              {deletePolicy === 'anonymize' ? 'Anonymize' : 'Delete'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}

// ── Sub-components ──────────────────────────────────────────────────────────

function Section({ title, icon: Icon, action, children }: {
  title: string
  icon?: React.ElementType
  action?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2">
        {Icon && <Icon className="w-3.5 h-3.5 text-muted-foreground" />}
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{title}</h3>
        {action && <div className="ml-auto">{action}</div>}
      </div>
      {children}
    </div>
  )
}

function InfoRow({ icon: Icon, label, value }: {
  icon: React.ElementType
  label: string
  value: string
}) {
  return (
    <div className="flex items-center gap-2 py-1">
      <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
      <span className="text-xs text-muted-foreground w-16 shrink-0">{label}</span>
      <span className="text-sm text-foreground truncate">{value}</span>
    </div>
  )
}

function MetricCard({ label, value, icon: Icon }: {
  label: string
  value: string
  icon: React.ElementType
}) {
  return (
    <div className="p-2.5 rounded-lg bg-muted border border-border">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <p className="text-sm font-semibold text-foreground">{value}</p>
    </div>
  )
}

function QuickActionButton({ label, icon: Icon, onClick, disabled }: {
  label: string
  icon: React.ElementType
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      leftIcon={<Icon className="w-4 h-4" />}
      className="w-full justify-start gap-2 font-normal text-muted-foreground"
    >
      {label}
    </Button>
  )
}
